/**
 * DoneScreen.tsx
 * @version 4.0.0 — Parcours Unifié (tout-en-queue)
 *
 * Changements v4.0.0 :
 *   - Parcours IDENTIQUE online/offline : saveImmediate → PDF → addToUploadQueue
 *   - Plus d'upload direct ni d'envoi d'emails ici
 *   - Le worker (useUploadWorker) gère upload MRC + 3 emails
 *   - Finalisation instantanée (~2s) au lieu de 30-90s
 *
 * UI conservée : EmailPanel, Bilan retour, triggerDownload, pipeline 2 étapes
 */

import React, { useState, useCallback, useRef, useEffect, memo } from 'react'
import {
  CheckCircle, Download, RotateCcw, Loader,
  Upload, Mail, Calendar, Image, ChevronLeft, AlertTriangle, WifiOff, Pencil, Check, RefreshCw,
} from 'lucide-react'
import { useAppStore } from '../store/useAppStore'
import { usePendingMrcStore } from '../store/usePendingMrcStore'
import { photoMemory } from '../store/photoMemoryStore'
import { offlinePhotoStore, InspectionType } from '../store/offlinePhotoStore'
import { generatePDF } from '../services/pdfService'
import { OLYMPIC_AGENCIES } from '../utils/agencies'
import { buildAuthHeader } from '../utils/auth'
import { BACKEND } from '../utils/config'
import { tryMakeInspectionKey, getMrcContractId } from '../utils/inspectionKey'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import toast from 'react-hot-toast'
import api from '../services/api'

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES (identiques v2.5.0)
// ─────────────────────────────────────────────────────────────────────────────

const OLYMPIC_DEFAULT_EMAIL = 'etatsolympiclocation@gmail.com'
const FUEL_LABELS = ['Vide', '1/8', '2/8', '3/8', '4/8', '5/8', '6/8', '7/8', 'Plein'] as const

// ✅ v3.0 — Timeout de sécurité sur isRunningRef
const FINALIZE_TIMEOUT_MS = 10_000

const PIPELINE_STEPS = [
  { id: 'generating' as const, label: 'Génération du PDF...' },
  { id: 'uploading' as const, label: 'Ajout à la file de synchronisation...' },
]

// ─────────────────────────────────────────────────────────────────────────────
// TYPES (identiques v2.5.0)
// ─────────────────────────────────────────────────────────────────────────────

type LocalStep = 'idle' | 'generating' | 'uploading' | 'done'

interface FinalizeResultsData {
  downloaded: boolean
  uploaded: boolean
  queued: boolean
  photosUploaded: number
  photosFailed: number
  emailClient: boolean
  emailAgency: boolean
  emailOlympic: boolean
  docId?: string
}

interface DepartData {
  fuel?: number
  mileage?: number
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS (identiques v2.5.0)
// ─────────────────────────────────────────────────────────────────────────────



function toInspectionType(t: string | undefined | null): InspectionType {
  return (t === 'depart' || t === 'retour') ? t : 'retour'
}

function fmtDateFull(d?: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '—' }
}

function fmtDateShort(d?: string | null): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '—' }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

function resolveAgencyEmail(
  contractAgencyEmail: string | undefined,
  selectedAgency: { id?: string; email?: string } | null | undefined
): string {
  if (contractAgencyEmail) return contractAgencyEmail
  if (selectedAgency?.id) {
    const found = OLYMPIC_AGENCIES.find(a => a.id === selectedAgency.id)
    if (found?.email) return found.email
  }
  if (selectedAgency?.email) return selectedAgency.email
  return ''
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a) }, 1_000)
}

// ─────────────────────────────────────────────────────────────────────────────
// UI ATOMS (identiques v2.5.0)
// ─────────────────────────────────────────────────────────────────────────────

type ColorKey = 'green' | 'red' | 'blue' | 'gray' | 'orange'

const BG_MAP: Record<ColorKey, string> = {
  green: 'bg-green-50 border-green-200',
  red: 'bg-red-50 border-red-200',
  blue: 'bg-blue-50 border-blue-200',
  gray: 'bg-gray-50 border-gray-200',
  orange: 'bg-orange-50 border-orange-200',
}
const TEXT_MAP: Record<ColorKey, string> = {
  green: 'text-green-800',
  red: 'text-red-800',
  blue: 'text-blue-800',
  gray: 'text-gray-700',
  orange: 'text-orange-800',
}

const InfoRow = memo(function InfoRow({
  emoji, bg, bold, children,
}: { emoji: string; bg: ColorKey; bold?: boolean; children: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-2 border rounded-xl px-3 py-2 ${BG_MAP[bg]}`}>
      <span className="text-lg flex-shrink-0">{emoji}</span>
      <p className={`text-sm ${TEXT_MAP[bg]} ${bold ? 'font-semibold' : 'font-medium'}`}>
        {children}
      </p>
    </div>
  )
})

const StatusRow = memo(function StatusRow({
  icon, label, ok, skipped, partial,
}: {
  icon: React.ReactNode
  label: string
  ok?: boolean
  skipped?: boolean
  partial?: boolean
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-gray-400 flex-shrink-0">{icon}</span>
      <span className="flex-1 text-gray-700">{label}</span>
      {skipped && <span className="text-gray-400 text-xs">Non configuré</span>}
      {!skipped && ok && <span className="text-green-600 font-semibold text-xs">✓ OK</span>}
      {!skipped && partial && !ok && <span className="text-orange-500 text-xs font-semibold">⚠ Partiel</span>}
      {!skipped && !ok && !partial && <span className="text-red-500 text-xs">✗ Échec</span>}
    </div>
  )
})

const PipelineProgress = memo(function PipelineProgress({
  currentStep,
}: { currentStep: LocalStep }) {
  const idx = PIPELINE_STEPS.findIndex(s => s.id === currentStep)
  return (
    <div className="flex flex-col gap-2">
      {PIPELINE_STEPS.map((step, i) => {
        const done = i < idx
        const active = i === idx
        return (
          <div key={step.id} className="flex items-center gap-3">
            <div className={[
              'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all',
              done ? 'bg-green-500 text-white' :
                active ? 'bg-blue-500 text-white animate-pulse' :
                  'bg-gray-200 text-gray-400',
            ].join(' ')}>
              {done ? '✓' : i + 1}
            </div>
            <span className={`text-sm ${active ? 'text-gray-800 font-semibold' :
              done ? 'text-green-700' :
                'text-gray-400'
              }`}>
              {step.label}
            </span>
          </div>
        )
      })}
    </div>
  )
})

const EmailRow = memo(function EmailRow({
  label, email, editable, onChange,
}: {
  label: string
  email: string
  editable?: boolean
  onChange?: (val: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [inputValue, setInputValue] = useState(email)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setInputValue(email) }, [email])

  const handleEdit = () => {
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleConfirm = () => {
    if (inputValue && !isValidEmail(inputValue)) { setError('Email invalide'); return }
    setError('')
    setEditing(false)
    onChange?.(inputValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirm()
    if (e.key === 'Escape') { setEditing(false); setInputValue(email); setError('') }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${editing ? 'border-blue-300 bg-white' : 'bg-gray-50 border-gray-200'
        }`}>
        <Mail size={14} className="text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide leading-none mb-0.5">
            {label}
          </p>
          {editing ? (
            <input
              ref={inputRef}
              type="email"
              value={inputValue}
              onChange={e => { setInputValue(e.target.value); setError('') }}
              onKeyDown={handleKeyDown}
              className="w-full text-sm text-gray-800 bg-transparent outline-none border-b border-blue-300"
            />
          ) : (
            <p className="text-sm text-gray-700 font-medium truncate">
              {email || <span className="text-gray-400 italic">Non renseigné</span>}
            </p>
          )}
        </div>
        {editable && (
          editing ? (
            <button
              onClick={handleConfirm}
              className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center transition-colors"
            >
              <Check size={14} className="text-green-600" />
            </button>
          ) : (
            <button
              onClick={handleEdit}
              className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 hover:bg-blue-100 flex items-center justify-center transition-colors"
            >
              <Pencil size={13} className="text-gray-500 hover:text-blue-500" />
            </button>
          )
        )}
      </div>
      {error && <p className="text-xs text-red-500 px-1">{error}</p>}
    </div>
  )
})

const EmailPanel = memo(function EmailPanel({
  clientEmail, agencyEmail, onClientEmailChange,
}: {
  clientEmail: string
  agencyEmail: string
  onClientEmailChange: (val: string) => void
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={15} className="text-blue-500 flex-shrink-0" />
        <h3 className="font-bold text-gray-700 text-sm">Envoi automatique des emails</h3>
      </div>
      <div className="flex flex-col gap-2">
        <EmailRow label="Client" email={clientEmail} editable onChange={onClientEmailChange} />
        <EmailRow label="Agence" email={agencyEmail} />
        <EmailRow label="Olympic Location" email={OLYMPIC_DEFAULT_EMAIL} />
      </div>
      <p className="text-xs text-gray-400 mt-3 text-center">
        ✉️ Les emails sont envoyés automatiquement après synchronisation
      </p>
    </Card>
  )
})

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────

function useRetourData(
  type: string | undefined,
  contractId: string | undefined,
  credentials: Record<string, string> | null,
): DepartData | null {
  const [departData, setDepartData] = useState<DepartData | null>(null)

  useEffect(() => {
    if (type !== 'retour' || !contractId || !credentials) return
    const controller = new AbortController()

    fetch(`${BACKEND}/api/edl/${contractId}`, {
      headers: { 'X-MRC-Auth': buildAuthHeader(credentials as any) },
      signal: controller.signal,
    })
      .then(r => r.json())
      .then((data: { depart?: DepartData }) => {
        if (data?.depart) setDepartData(data.depart)
      })
      .catch(err => {
        if ((err as Error).name !== 'AbortError')
          console.warn('[useRetourData]', (err as Error).message)
      })

    return () => controller.abort()
  }, [type, contractId, credentials])

  return departData
}

// ─────────────────────────────────────────────────────────────────────────────
// ✅ v4.0 — useFinalizeInspection — PARCOURS UNIFIÉ
// Online ou offline, le parcours est IDENTIQUE :
//   1. saveImmediate()     → IndexedDB sécurisé
//   2. generatePDF()       → PDF local
//   3. triggerDownload()   → téléchargement agent
//   4. addToUploadQueue()  → TOUJOURS → le worker gère upload + emails
// ─────────────────────────────────────────────────────────────────────────────

function useFinalizeInspection(
  clientEmail: string,
  agencyEmail: string,
  departData?: DepartData | null,
) {
  const inspection = useAppStore(s => s.inspection)
  const addToUploadQueue = useAppStore(s => s.addToUploadQueue)
  const saveImmediate = useAppStore(s => s.saveImmediate)
  const credentials = useAppStore(s => s.credentials)
  const addPendingReturn = usePendingMrcStore(s => s.addPendingReturn)

  const [localStep, setLocalStep] = useState<LocalStep>('idle')
  const [results, setResults] = useState<FinalizeResultsData | null>(null)
  const isRunningRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { contract, type } = inspection

  const dateRetourReelle = useRef(
    inspection.completedAt ?? new Date().toISOString()
  ).current

  // Nettoyage timeout au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleFinalize = useCallback(async () => {
    if (!contract?.id || !type) {
      toast.error('Données du contrat manquantes')
      return
    }
    if (isRunningRef.current) {
      toast('Traitement déjà en cours...', { icon: '⏳' })
      return
    }

    isRunningRef.current = true
    setLocalStep('generating')
    const toastId = toast.loading('Génération du PDF...')

    // Timeout 10s de sécurité
    timeoutRef.current = setTimeout(() => {
      if (isRunningRef.current) {
        isRunningRef.current = false
        setLocalStep('idle')
        toast.error(
          'La finalisation prend trop de temps.\n' +
          'Vos données sont sauvegardées localement — réessayez.',
          { id: toastId, duration: 6_000 }
        )
        console.warn('[DoneScreen] ⏱ Timeout 10s — isRunningRef débloqué')
      }
    }, FINALIZE_TIMEOUT_MS)

    try {
      const freshInspection = photoMemory.injectInto(useAppStore.getState().inspection)
      const freshPhotoCount = photoMemory.getPhotoCount()
      // ✅ v5.0 — Utiliser inspectionKey depuis le store (déterministe)
      const inspectionKey = freshInspection.inspectionKey
        ?? tryMakeInspectionKey(type, contract)
      // contractKey pour l'API MRC (ID numérique)
      const contractKey = String(contract.rawId ?? contract.id)
      const inspType = toInspectionType(type)

      // ── 1. Persistance IndexedDB ──────────────────────────────────────
      if (freshPhotoCount > 0 && inspectionKey) {
        await offlinePhotoStore.savePhotos(
          inspectionKey, freshInspection.photos, inspType,
        ).catch(e => console.warn('[DoneScreen] IndexedDB save photos:', (e as Error).message))
      }

      await saveImmediate(contractKey, inspType, {
        ...freshInspection,
        inspectionKey,
        step: 'done',
        savedAt: new Date().toISOString(),
      }).catch((e: Error) => console.warn('[DoneScreen] saveImmediate:', e.message))

      console.log('[DoneScreen] ✅ saveImmediate — brouillon sécurisé')

      // ── 2. Génération PDF local ───────────────────────────────────────
      const pdfResult = await generatePDF(freshInspection)
      if (!pdfResult?.pdfBlob || !pdfResult?.pdfBase64)
        throw new Error(`generatePDF invalide: ${JSON.stringify(Object.keys(pdfResult ?? {}))}`)

      const { pdfBlob } = pdfResult
      const pdfFilename = `edl-${type}-${contract.plate}-${new Date().toISOString().slice(0, 10)}.pdf`

      const blob = pdfBlob instanceof Blob ? pdfBlob : new Blob([pdfBlob], { type: 'application/pdf' })
      triggerDownload(blob, pdfFilename)
      toast.success('PDF téléchargé ✓', { id: toastId })

      // ── 3. Ajout à la file de synchronisation (ou upload prep direct) ──
      setLocalStep('uploading')

      const isPrep = !!(contract as any)?._isPrep

      if (isPrep) {
        // ── MODE PRÉPARATION : upload direct vers /api/edl/prep/save ────
        toast.loading('Publication de la préparation...', { id: toastId })
        try {
          const photos = photoMemory.getPhotos() as Record<string, any>
          const dualPhotos: Record<string, any> = {}
          for (const [zone, photoData] of Object.entries(photos)) {
            if (typeof photoData === 'string') {
              dualPhotos[zone] = photoData
            } else if (photoData?.dataUrl) {
              dualPhotos[zone] = { dataUrl: photoData.dataUrl, originalDataUrl: photoData.originalDataUrl }
            }
          }

          await api.post('/api/edl/prep/save', {
            immat: contract.plate,
            type: type,
            vehicleName: contract.vehicleName || contract.plate,
            mileage: freshInspection.mileage,
            fuel: freshInspection.fuel,
            damages: freshInspection.damages ?? [],
            photos: dualPhotos,
            agentName: (useAppStore.getState().credentials as any)?.username || 'Agent',
            signatureAgent: freshInspection.signatureAgent,
            securityCheck: freshInspection.securityCheck,
            completedAt: dateRetourReelle,
            notes: '',
          }, { timeout: 120_000 }) // 120s — payload lourd avec photos base64
          toast.success('Préparation publiée ✅', { id: toastId, duration: 4_000 })
        } catch (prepErr: any) {
          console.error('[DoneScreen] Erreur upload prep:', prepErr)
          const isNetwork = prepErr?.message === 'Network Error' || prepErr?.code === 'ECONNABORTED'
          const msg = isNetwork
            ? 'Connexion perdue — vérifiez votre réseau et réessayez'
            : (prepErr?.response?.data?.error || prepErr?.message || 'Erreur inconnue')
          toast.error(`Erreur publication : ${msg}`, { id: toastId, duration: 6_000 })
          setLocalStep('idle')
          return
        }
      } else {
        // ── MODE NORMAL : ajout à la queue d'upload ──────────────────────
        toast.loading('Ajout à la file de synchronisation...', { id: toastId })

        addToUploadQueue({
          contractId: contractKey,
          type,
          inspection: {
            mileage: freshInspection.mileage,
            fuel: freshInspection.fuel,
            damages: freshInspection.damages ?? [],
            completedAt: dateRetourReelle,
            startedAt: freshInspection.startedAt,
            deviceInfo: freshInspection.deviceInfo ?? null,
            signatureAgent: freshInspection.signatureAgent,
            signatureClient: freshInspection.signatureClient,
            securityCheck: freshInspection.securityCheck,
            accidentDeclaration: freshInspection.accidentDeclaration,
            photosChecked: freshInspection.photosChecked,
            contract: {
              ...contract,
              clientEmail: clientEmail,
              agencyEmail: agencyEmail,
              // ✅ FIX — Sauvegarder agenceDepartCode dans la queue
              // Le worker en a besoin pour construire l'agencyCode de l'alerte MRC
              // même si l'app est rechargée entre la finalisation et l'upload.
              agenceDepartCode: contract.agenceDepartCode || null,
            },
          },
        })

        if (type === 'retour' && !isPrep) {
          addPendingReturn({
            id: contractKey,
            plate: contract.plate || 'Inconnu',
            vehicleName: contract.vehicleName || 'Véhicule',
            clientName: contract.clientName || 'Client',
            agencyCode: String(contract.agenceDepartCode || ''),
            completedAt: dateRetourReelle,
            agentName: (credentials as any)?.username || 'Agent',
            kmDepart: departData?.mileage,
            kmRetour: freshInspection.mileage,
            fuelDepart: departData?.fuel,
            fuelRetour: freshInspection.fuel,
          });
        }
      }

      // ── 4. Résultat instantané ────────────────────────────────────────
      const res: FinalizeResultsData = {
        downloaded: true,
        uploaded: false,
        queued: true,
        photosUploaded: 0,
        photosFailed: 0,
        emailClient: false,
        emailAgency: false,
        emailOlympic: false,
      }

      setResults(res)
      setLocalStep('done')

      const isPrep2 = !!(contract as any)?._isPrep
      if (isPrep2) {
        toast.success('Préparation publiée avec succès ✅', { id: toastId, duration: 4_000 })
      } else {
        toast.success(
          navigator.onLine
            ? 'PDF téléchargé — synchronisation en cours…'
            : 'PDF téléchargé — sync automatique au retour réseau 📶',
          { id: toastId, duration: 4_000 }
        )
      }

    } catch (err) {
      console.error('[DoneScreen] ERREUR:', err)
      toast.error(`Erreur : ${(err as Error).message}`, { id: toastId })
      setLocalStep('idle')
    } finally {
      isRunningRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [
    contract, type, dateRetourReelle,
    addToUploadQueue, saveImmediate, credentials,
    clientEmail, agencyEmail,
    departData,
  ])

  return { localStep, results, handleFinalize, dateRetourReelle }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS (identiques v2.5.0)
// ─────────────────────────────────────────────────────────────────────────────

const RetourBilan = memo(function RetourBilan({
  fuelDepart, fuelRetour, kmDepart, kmRetour,
}: {
  fuelDepart: number | null
  fuelRetour: number | null
  kmDepart: number | null
  kmRetour: number | null
}) {
  const kmParcourus = kmRetour != null && kmDepart != null ? kmRetour - kmDepart : null
  const fuelOk = fuelDepart != null && fuelRetour != null && fuelRetour >= fuelDepart
  const fuelAlerte = fuelDepart != null && fuelRetour != null && fuelRetour < fuelDepart

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h3 className="font-bold text-gray-700 text-sm">Bilan retour</h3>
      {fuelOk && (
        <InfoRow emoji="✅" bg="green">
          Carburant OK — départ {FUEL_LABELS[fuelDepart!]} = retour {FUEL_LABELS[fuelRetour!]}
        </InfoRow>
      )}
      {fuelAlerte && (
        <InfoRow emoji="⚠️" bg="red" bold>
          Manque carburant : départ {FUEL_LABELS[fuelDepart!]} → retour {FUEL_LABELS[fuelRetour!]}
        </InfoRow>
      )}
      {fuelDepart == null && (
        <InfoRow emoji="⛽" bg="gray">Niveau carburant départ non disponible</InfoRow>
      )}
      {kmParcourus != null ? (
        <InfoRow emoji="🛣️" bg="blue">
          Kilomètres parcourus : <strong>{kmParcourus} km</strong>
          <span className="text-blue-500 text-xs ml-2">({kmDepart} → {kmRetour})</span>
        </InfoRow>
      ) : (
        <InfoRow emoji="🛣️" bg="gray">Kilométrage départ non disponible</InfoRow>
      )}
    </Card>
  )
})

const FinalizeResultsPanel = memo(function FinalizeResultsPanel({
  results, photoCount, hasClientEmail, onViewQueue,
}: {
  results: FinalizeResultsData
  photoCount: number
  hasClientEmail: boolean
  onViewQueue?: () => void
}) {
  return (
    <Card className="p-4">
      <h3 className="font-bold text-gray-700 mb-3 text-sm">Résultats</h3>
      <div className="flex flex-col gap-2">
        <StatusRow icon={<Download size={15} />} label="PDF téléchargé" ok={results.downloaded} />
        {results.queued ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-400 flex-shrink-0"><Upload size={15} /></span>
              <span className="flex-1 text-gray-700">Sync myrentcar</span>
              <span className="text-orange-500 text-xs font-semibold">⏳ En attente réseau</span>
            </div>
            {onViewQueue && (
              <button
                onClick={onViewQueue}
                className="w-full mt-1 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg py-2 transition-colors"
              >
                📋 Voir la file d'attente
              </button>
            )}
          </div>
        ) : (
          <>
            <StatusRow icon={<Upload size={15} />} label="PDF intégré dans myrentcar" ok={results.uploaded} />
            <StatusRow
              icon={<Image size={15} />}
              label={`Photos myrentcar (${results.photosUploaded}/${photoCount})`}
              ok={results.photosUploaded === photoCount && photoCount > 0}
              partial={results.photosUploaded > 0 && results.photosUploaded < photoCount}
              skipped={photoCount === 0}
            />
            <StatusRow
              icon={<Mail size={15} />}
              label="Email client"
              ok={results.emailClient}
              skipped={!hasClientEmail}
            />
            <StatusRow icon={<Mail size={15} />} label="Email agence" ok={results.emailAgency} />
            <StatusRow icon={<Mail size={15} />} label="Email Olympic Location" ok={results.emailOlympic} />
          </>
        )}
      </div>
    </Card>
  )
})

// ─────────────────────────────────────────────────────────────────────────────
// ✅ Banner "Sync échouée" — visible quand l'item de queue passe en 'failed'
// ─────────────────────────────────────────────────────────────────────────────

const SyncFailedBanner = memo(function SyncFailedBanner({
  contractId,
  onViewQueue,
  onRetry,
}: {
  contractId: string | undefined
  onViewQueue: () => void
  onRetry: () => void
}) {
  const uploadQueue = useAppStore(s => s.uploadQueue)
  if (!contractId || contractId === 'undefined') return null

  const item = (uploadQueue as any[]).find(i => i.contractId === contractId)
  if (!item || item.status !== 'failed') return null

  return (
    <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-red-800 text-sm">⚠️ Synchronisation échouée</p>
          <p className="text-red-600 text-xs mt-1 leading-snug">
            L'EDL est sauvegardé localement mais n'a pas pu être envoyé au serveur.
            Vos données ne sont <strong>pas perdues</strong>.
          </p>
          <p className="text-red-500 text-xs mt-1">
            {item.retries ?? 0} tentative{(item.retries ?? 0) > 1 ? 's' : ''} échouée{(item.retries ?? 0) > 1 ? 's' : ''} — Reconnectez-vous et réessayez.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onRetry}
          className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-2.5 rounded-xl transition-colors active:scale-[0.97]"
        >
          <RefreshCw size={15} /> Réessayer maintenant
        </button>
        <button
          onClick={onViewQueue}
          className="px-3 py-2.5 border-2 border-red-300 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-50 transition-colors"
        >
          File d'attente
        </button>
      </div>
    </div>
  )
})

// ─────────────────────────────────────────────────────────────────────────────
// ÉCRAN PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export default function DoneScreen() {
  const inspection = useAppStore(s => s.inspection)
  const credentials = useAppStore(s => s.credentials)
  const selectedAgency = useAppStore(s => s.selectedAgency)
  const resetInspection = useAppStore(s => s.resetInspection)
  const setStep = useAppStore(s => s.setStep)
  const retryQueueItem = useAppStore(s => s.retryQueueItem)

  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  const { contract, type, damages } = inspection
  const isPrep = !!(contract as any)?._isPrep
  const photoCount = photoMemory.getPhotoCount()

  const resolvedAgencyEmail = resolveAgencyEmail(contract?.agencyEmail, selectedAgency)
  const [clientEmail, setClientEmail] = useState(contract?.clientEmail ?? '')

  const departData = useRetourData(
    type,
    contract?.rawId ?? contract?.id,
    credentials,
  )

  const { localStep, results, handleFinalize, dateRetourReelle } =
    useFinalizeInspection(clientEmail, resolvedAgencyEmail, departData)

  useEffect(() => {
    const on = () => setIsOffline(false)
    const off = () => setIsOffline(true)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  // ✅ Handler retry depuis le banner d'échec sync
  // retryQueueItem remet l'item en 'pending' → le poll (10s) relance automatiquement
  const handleRetrySync = useCallback(() => {
    const contractKey = String(contract?.rawId ?? contract?.id)
    if (!contractKey) return
    const queue = useAppStore.getState().uploadQueue as any[]
    const item = queue.find(i => i.contractId === contractKey)
    if (item) retryQueueItem(item.id)
  }, [contract, retryQueueItem])

  const handleNewInspection = useCallback(() => {
    const selectionQueue = useAppStore.getState().selectionQueue
    resetInspection()
    if (isPrep) {
      setStep('prep-inspect')
    } else if (selectionQueue && selectionQueue.length > 0) {
      setStep('selection')
    }
  }, [resetInspection, setStep, isPrep])
  const isLoading = PIPELINE_STEPS.some(s => s.id === localStep)
  const contractKey = String(contract?.rawId ?? contract?.id)

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex flex-col items-center justify-center p-5">
      <div className="max-w-lg w-full flex flex-col gap-5">

        {/* Header */}
        <div className="text-center">
          <CheckCircle size={68} className={isPrep ? 'text-amber-500 mx-auto mb-3' : 'text-green-500 mx-auto mb-3'} />
          <h1 className="text-2xl font-black text-gray-800">
            {isPrep ? 'Préparation publiée !' : 'État des lieux terminé !'}
          </h1>
          <p className="text-gray-500 mt-1">
            {type === 'depart' ? 'Départ' : 'Retour'} — {contract?.plate} — {contract?.vehicleName}
          </p>
        </div>

        {/* Hors ligne */}
        {isOffline && (
          <div className="bg-orange-50 border border-orange-300 rounded-xl p-3 flex items-center gap-2">
            <WifiOff size={16} className="text-orange-500 flex-shrink-0" />
            <p className="text-orange-700 text-sm font-semibold">
              Hors ligne — PDF local, sync automatique au retour réseau
            </p>
          </div>
        )}

        {/* ✅ Banner sync échouée — visible si l'upload a été rejeté après tous les retries */}
        <SyncFailedBanner
          contractId={contractKey}
          onViewQueue={() => setStep('queue')}
          onRetry={handleRetrySync}
        />

        {/* Erreur données manquantes */}
        {(!contract?.id || !type) && (
          <div className="bg-red-100 border border-red-400 rounded-xl p-3 text-red-700 text-sm font-semibold text-center flex items-center gap-2 justify-center">
            <AlertTriangle size={16} />
            Données manquantes — contract.id: {String(contract?.id)} | type: {String(type)}
          </div>
        )}

        {/* Stats */}
        <Card className="p-5">
          <div className="grid grid-cols-3 gap-3 text-center">
            {([
              { value: photoCount, label: 'Photos', color: 'blue' },
              { value: damages?.length ?? 0, label: 'Dommages', color: 'red' },
              { value: inspection.mileage ?? '—', label: 'km', color: 'green' },
            ] as const).map(({ value, label, color }) => (
              <div key={label} className={`bg-${color}-50 rounded-xl p-3`}>
                <p className={`text-2xl font-black text-${color}-600`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Dates retour */}
        {type === 'retour' && (
          <Card className="p-4 flex flex-col gap-2">
            <h3 className="font-bold text-gray-700 text-sm flex items-center gap-2">
              <Calendar size={15} /> Dates de retour
            </h3>
            <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
              <span className="text-base flex-shrink-0 mt-0.5">📅</span>
              <div>
                <p className="text-xs font-bold text-orange-700 uppercase tracking-wide">Retour réel</p>
                <p className="text-sm font-semibold text-orange-900">{fmtDateFull(dateRetourReelle)}</p>
              </div>
            </div>
            {contract?.endDate && (
              <div className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <span className="text-base flex-shrink-0 mt-0.5">🗓️</span>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Retour prévu</p>
                  <p className="text-sm text-gray-600">{fmtDateShort(contract.endDate)}</p>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Bilan retour */}
        {type === 'retour' && (
          <RetourBilan
            fuelDepart={departData?.fuel ?? null}
            fuelRetour={inspection.fuel ?? null}
            kmDepart={departData?.mileage ?? null}
            kmRetour={inspection.mileage ?? null}
          />
        )}

        {/* Pipeline en cours */}
        {isLoading && (
          <Card className="p-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Loader size={20} className="animate-spin text-blue-500 flex-shrink-0" />
              <p className="text-gray-700 font-semibold text-sm">Finalisation en cours…</p>
            </div>
            <PipelineProgress currentStep={localStep} />
          </Card>
        )}

        {/* Résultats */}
        {results && (
          <FinalizeResultsPanel
            results={results}
            photoCount={photoCount}
            hasClientEmail={!!clientEmail}
            onViewQueue={() => setStep('queue')}
          />
        )}

        {/* EmailPanel + boutons (état idle uniquement) */}
        {localStep === 'idle' && (
          <div className="flex flex-col gap-3">
            <EmailPanel
              clientEmail={clientEmail}
              agencyEmail={resolvedAgencyEmail}
              onClientEmailChange={setClientEmail}
            />
            <Button
              onClick={() => setStep('photos')}
              variant="ghost"
              size="lg"
              className="w-full text-gray-500"
            >
              <ChevronLeft size={18} /> Retour aux photos
            </Button>
            <Button
              onClick={handleFinalize}
              size="xl"
              className="w-full"
              disabled={!contract?.id || !type}
            >
              <Download size={22} /> Générer le PDF & finaliser
            </Button>
          </div>
        )}

        {/* Nouvel EDL */}
        {localStep === 'done' && (
          <Button
            onClick={handleNewInspection}
            variant="secondary"
            size="lg"
            className="w-full"
          >
            <RotateCcw size={20} /> {isPrep ? 'Nouvelle préparation' : 'Nouvel état des lieux'}
          </Button>
        )}

      </div>
    </div>
  )
}